<template>
  <div class="card">
    <div class="card-top">
      <h1>{{ country.text }}</h1>
    </div>
    <div class="content">
      <basic-settings :survey="survey" :country="country" />

      <advanced-settings :survey="survey" :country="country" />

      <div>
        <button
          @click="status === 'live' ? openModalSave() : saveSettings()"
          class="btn-save"
        >
          <span v-if="!loadingSaveSettings">{{ parseTranslation(APPLY) }}</span>
          <span v-else> {{ parseTranslation(LOADING) }}...</span>
        </button>
      </div>

      <div v-if="hasSampleApi && !loadingSaveSettings && survey.sample">
        <wanted-completes
          :survey="survey"
          :desiredCompletes="survey.sample.desired_completes"
        />
        <quota-panel :survey="survey" />

        <div class="btns">
          <button @click="openModalDelete" class="btn-launch delete">
            <span v-if="!loadingDelete">{{ parseTranslation(CANCEL) }}</span>
            <span v-else> {{ parseTranslation(LOADING) }}...</span>
          </button>

          <button @click="saveQuota" class="btn-launch">
            <span>{{ translations[language][SAVE_SETTINGS] }}</span>
          </button>

          <button @click="openModalLaunch(status)" class="btn-launch save">
            <span v-if="!loadingUpload && status === 'live'">{{
              translations[language][PAUSE_SAMPLE]
            }}</span>

            <span v-else-if="!loadingUpload && (status === 'pause' || !status)">{{
              translations[language][LAUNCH_SAMPLE]
            }}</span>

            <span v-else> {{ parseTranslation(LOADING) }}...</span>
          </button>
        </div>
      </div>

      <div v-else-if="loadingSaveSettings">
        <Loader />
      </div>
    </div>
  </div>
</template>

<script>
import BasicSettings from './BasicSettings.vue'
import AdvancedSettings from './AdvancedSettings.vue'

import QuotaPanel from '@/pages/Quota/QuotaPanel.vue'
import WantedCompletes from '@/pages/Quota/WantedCompletes.vue'

import Loader from '@/components/loading/Loader-2'

import { mapActions, mapState, mapGetters } from 'vuex'

import * as translationCountry from './index.translations'
const { default: translations, ...translationKeys } = translationCountry

export default {
  props: {
    country: {
      required: true,
    },

    survey: {
      required: true,
    },

    getSurvey: {
      required: true,
    },
  },

  components: {
    BasicSettings,
    AdvancedSettings,
    QuotaPanel,
    Loader,
    WantedCompletes,
  },

  data() {
    return {
      translations,
      ...translationKeys,
      loadingSaveSettings: false,
      loadingDelete: false,
      loadingUpload: false,
      status: null,
    }
  },

  computed: {
    ...mapState([
      'wantedCompletes',
      'basicQuestions',
      'advancedQuestions',
      'quotaJSON',
      'completeChecker',
      'quotaOrder',
    ]),

    ...mapGetters(['isLoadingSaveSettingsQuota']),

    sampleApiGroup() {
      return {
        group_code: 'Sample',
        group_name: 'Sample API Group',
        questions: [...this.basicQuestions, ...this.advancedQuestions],
      }
    },

    hasSampleApi() {
      return !!this.survey.questions_groups.find(group => group.group_code === 'Sample')
    },
  },

  methods: {
    ...mapActions([
      'modalBtns',
      'sendMessage',
      'setStep',
      'executeSurveyAPICall',
      'setCreatedProject',
      'setLoadingSaveSettingsQuota',
    ]),

    validateAgeRange() {
      const minValue = 13

      const questionAge = this.sampleApiGroup?.questions.find(
        question => question.question === 'Age',
      )

      const invalidAge = questionAge?.answers.find(answer => {
        const [min, max] = answer?.label.split('-')

        return Number(min) < minValue || Number(max) < minValue
      })

      return !!invalidAge
    },

    async saveSettings() {
      if (this.loadingSaveSettings) return

      if (this.validateAgeRange()) {
        return this.sendMessage(this.parseTranslation(this.VALIDATION_AGE_RANGE))
      }

      const updatedSurvey = await this.executeSurveyAPICall({
        path: 'survey/sample',
        method: 'POST',
        payload: {
          survey_id: this.survey.id_,
          sampleGroup: this.sampleApiGroup,
          sample: {
            country: this.country.value,
          },
        },
      })

      if (!updatedSurvey) return

      this.sendMessage(this.translations[this.language][this.CHANGES_SAVED])

      // THIS SHOULD BE CHANGED: YOU HAVE THE UPDATED SURVEY OBJECT AFTER THE REQUEST
      await this.getSurvey()
    },

    async saveQuota() {
      if (this.isLoadingSaveSettingsQuota || this.completeChecker) return

      if (this.wantedCompletes < 1) {
        return this.sendMessage(this.parseTranslation(this.VALIDATION_WANTED_COMPLETES))
      }

      this.setLoadingSaveSettingsQuota(true)

      const updatedSurvey = await this.executeSurveyAPICall({
        path: 'survey/quota',
        method: 'POST',
        payload: {
          survey_id: this.survey.id_,
          quota: this.quotaJSON,
          quota_order: this.quotaOrder,
          sample: {
            desired_completes: `${this.wantedCompletes}`,
            country: this.country.value,
          },
        },
      })

      if (!updatedSurvey) {
        return this.setLoadingSaveSettingsQuota(false)
      }

      const sampleSuccess = await this.executeSurveyAPICall({
        path: 'sample/create-project',
        method: 'POST',
        payload: {
          survey_id: this.survey.id_,
        },
      })

      if (!sampleSuccess) {
        return this.setLoadingSaveSettingsQuota(false)
      }

      this.setCreatedProject(!!sampleSuccess)

      this.sendMessage(this.parseTranslation(this.CHANGES_SAVED))

      this.setLoadingSaveSettingsQuota(false)

      await this.getSurvey()
    },

    openModalSave() {
      if (!this.loadingSaveSettings) {
        const activated = this.survey.activated
        const modalWarning = true

        this.modalBtns({
          text: this.translations[this.language][this.MODAL_SAVE],

          yes: this.saveSettings,

          params: { activated, modalWarning, confirmPause: true },
        })
      }
    },

    openModalLaunch(status) {
      if (this.completeChecker) return

      const activated = this.survey.activated

      const textKey = status === 'live' ? this.PAUSE_SAMPLE_MESSAGE : this.START_FIELD

      this.modalBtns({
        text: this.parseTranslation(textKey),
        yes: this.upload,
        params: { activated },
      })
    },

    async changeStatus(status) {
      if (this.loadingUpload) return

      this.loadingUpload = true

      const statusUpdate = await this.executeSurveyAPICall({
        path: 'sample/status',
        method: 'POST',
        payload: {
          survey_id: this.survey.id_,
          status,
        },
      })

      if (!statusUpdate) return (this.loadingUpload = false)

      this.status = status

      const textKey = status === 'live' ? this.SURVEY_LAUNCHED : this.SURVEY_PAUSED

      this.sendMessage(this.parseTranslation(textKey))

      this.loadingUpload = false

      await this.getSurvey()
    },

    async upload() {
      const status = !this.status || this.status === 'pause' ? 'live' : 'pause'

      this.changeStatus(status)
    },

    openModalDelete() {
      const activated = this.survey.activated

      this.modalBtns({
        text: this.translations[this.language][this.WANT_DELETE],
        yes: this.deleteSample,
        params: { activated },
      })
    },

    async deleteSample() {
      if (this.loadingDelete) return

      this.loadingDelete = true

      const removedSample = this.survey.questions_groups.filter(
        group => group.group_code !== 'Sample',
      )

      const updatedSurvey = await this.executeSurveyAPICall({
        path: 'survey/quota',
        method: 'POST',
        payload: {
          survey_id: this.survey.id_,
          questions_groups: removedSample,
          quota: {},
          sample: {},
        },
      })

      if (!updatedSurvey) return (this.loadingDelete = false)

      this.setStep(1)

      this.sendMessage(this.translations[this.language][this.SURVEY_UPDATED])

      this.loadingDelete = false
    },
  },

  created() {
    if (this.survey.sample && this.survey.sample.status)
      this.status = this.survey.sample.status
  },
}
</script>

<style lang="scss" scoped>
.card {
  background: $main-4;
  border: 1px solid $main-4;
  margin-top: 20px;
  padding: 20px;
  margin-bottom: 80px;

  .card-top {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    h1 {
      @include tipography(23);
      font-weight: bold;
      color: $white;
    }
    img {
      display: none;
      animation: show 0.5s forwards;
      &.active {
        display: block;
      }
    }
  }

  .light & {
    background: $gray-light-6;
    border: 1px solid $gray-light-7;

    .card-top {
      h1 {
        color: $gray-2;
      }
    }
  }

  .content {
    position: relative;
    z-index: 20000;
  }

  .btn-save {
    display: block;
    @include tipography(16);
    background: $main;
    font-weight: bold;
    padding: 5px 30px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    margin: 30px 0 30px auto;
    &:hover {
      background: $gray-8;
    }
    &:focus {
      outline: none;
    }
  }

  .btns {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .btn-launch {
    display: block;
    @include tipography(15);
    background: $transparent;
    color: $white;
    font-weight: bold;
    padding: 10px 20px;
    border: 1px solid $white;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    margin-left: 10px;

    &:hover {
      background: $gray-8;
      color: $white;
      border: 1px solid $gray-8;
      transform: scale(1.05);
    }

    &:focus {
      outline: none;
    }

    &.delete {
      border: 1px solid $white;
      background: $transparent;
      color: $white;

      &:hover {
        background: $pink;
        color: white;
        border: 1px solid $pink;
      }
    }

    &.save {
      background: $transparent;
      color: white;

      &:hover {
        background: $gray-8;
      }
    }
  }

  .light & {
    .btn-launch {
      background: $white;
      color: $main;
      border: 1px solid $main;

      &:hover {
        background: $gray-8;
        color: $white;
        border: 1px solid $gray-8;
        transform: scale(1.05);
      }

      &.delete {
        border: 1px solid $pink;
        background: $white;
        color: $pink;

        &:hover {
          background: $pink;
          color: white;
          border: 1px solid $pink;
        }
      }

      &.save {
        background: $main;
        color: white;

        &:hover {
          background: $gray-8;
        }
      }
    }
  }
}

@media (max-width: 767.98px) {
  .btns {
    flex-direction: column;

    .btn-launch {
      margin-bottom: 10px;
      width: 170px;
    }
  }
}
</style>
